import React, { FC, useEffect, useImperativeHandle, useMemo } from 'react';

import { Alert } from '@kontentino/ui';
import { ApiClientError } from 'api/client';
import ContentPlanApi from 'app/api/contentPlan';
import PostTimeSaversApi, {
  MovePostSettledResult,
  ShiftToProjectParams,
} from 'app/api/postTimeSavers';
import { TYPE as POST_TYPE } from '@kontentino/kontentino-constants/Posts';
import { TYPE as PAGE_TYPE } from '@kontentino/kontentino-constants/Pages';
import { ShiftPostsTargetValidationError } from 'app/modules/posts/components/shiftPosts/ShiftTargetProfilesSelect';
import {
  ShiftPostsTarget,
  ShiftPostsVariant,
} from 'app/modules/posts/hooks/useShiftPosts';
import { getPostTargetInfoMessage } from 'app/modules/posts/utils/getPostTargetInfoMessage';
import UserpilotUtils from 'app/utils/userpilot';
import { useG2ReviewTrigger } from 'app/hooks/useG2ReviewTrigger';
import { queryKey } from 'constants/queryKey';
import routes, { modalRoutes } from 'constants/routes';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useMutation, useQuery } from 'react-query';
import { PageData } from 'types/Page';
import { Post } from 'types/Post';
import { ListPost } from 'types/PostDetail';
import useSetState from 'utils/hooks/useSetState';
import { createSearchFromParams, getBasePath } from 'utils/url';
import ShiftTargetProjectOfProfileSelect from './shiftPostToTarget/ShiftTargetProjectOfProfileSelect';
import ShiftTargetSuccessful from './shiftPostToTarget/ShiftTargetSuccessful';
import ShiftTargetUnsuccessful from './shiftPostToTarget/ShiftTargetUnsuccessful';
import { usePostsLayoutState } from 'app/modules/posts/hooks/usePostsLayoutState';

type PostsCustomSchedule = Record<
  number,
  {
    customScheduleDate?: string;
    customScheduleTime?: string;
  }
>;

type Props = {
  targetNumber: number;
  initialTarget: ShiftPostsTarget;
  onRemoveTarget?(): void;
  ref: React.Ref<TargetHandler>;
  error?: ShiftPostsTargetValidationError;
  variant: ShiftPostsVariant;
  availablePages: PageData[];
  dataNamePrefix: string;
  selectedPosts: ListPost[];
  shiftDays?: number;
  postsCustomSchedule?: PostsCustomSchedule;
};

export type TargetHandler = {
  getValidationError(): ShiftPostsTargetValidationError;
  submitDuplication(ids: number[]): void;
};

const ShiftPostToTarget: FC<Props> = React.forwardRef(
  (
    {
      targetNumber,
      onRemoveTarget,
      initialTarget,
      error,
      variant,
      availablePages,
      dataNamePrefix,
      selectedPosts,
      shiftDays,
      postsCustomSchedule,
    },
    ref: React.Ref<TargetHandler>,
  ) => {
    const { t } = useTranslation();
    const { triggerG2Review } = useG2ReviewTrigger();
    const isNewPostExperienceActive = usePostsLayoutState(
      (state) => state.isNewPostExperienceActive,
    );
    const shiftPost = useMutation<
      Post[] | MovePostSettledResult[],
      ApiClientError,
      ShiftToProjectParams
    >(PostTimeSaversApi.shiftToProject, {
      onSuccess(data) {
        if (variant === 'move') {
          const hasSuccess = (data as MovePostSettledResult[]).some(
            (result) => result.status === 'fulfilled',
          );
          if (!hasSuccess) return;
        }

        UserpilotUtils.track(
          variant === 'duplicate'
            ? UserpilotUtils.events.POSTS_POST_DUPLICATED
            : UserpilotUtils.events.POSTS_POST_MOVED,
        );
        triggerG2Review();
      },
    });
    const [target, setTarget] = useSetState({
      profileId: initialTarget.profileId,
      projectId: initialTarget.projectId,
      cloneAsDraft: false,
    });

    const profile = useMemo(() => {
      return availablePages.find((page) => page.id === target.profileId);
    }, [availablePages, target.profileId]);

    const availablePagesIds = availablePages.map((page) => page.id);

    const { data: projectsData, isLoading: isLoadProjectsLoading } = useQuery(
      queryKey.postsContentPlans(availablePagesIds),
      () => ContentPlanApi.getPagesContentPlans(availablePagesIds),
    );

    const projects = useMemo(() => {
      if (!projectsData?.data || !target.profileId) return [];
      return projectsData.data[target.profileId] || [];
    }, [projectsData, target.profileId]);

    const project = useMemo(() => {
      if (target.projectId) {
        return projects?.find((project) => project.id === target.projectId);
      }
    }, [projects, target]);

    useEffect(() => {
      if (projects.length > 0 && !target.projectId) {
        setTarget({ projectId: projects[0].id });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects, setTarget]);

    useImperativeHandle(ref, () => ({
      getValidationError: () => ({
        id: initialTarget.id,
        profileId: target.profileId ? undefined : t('isRequired'),
        projectId: isLoadProjectsLoading
          ? t('pleaseWaitProjectIsLoading')
          : target.projectId
          ? undefined
          : t('isRequired'),
      }),
      submitDuplication: (postIds: number[]) => {
        if (target.projectId) {
          shiftPost.mutate({
            variant,
            ids: postIds,
            projectId: target.projectId,
            cloneAsDraft: target.cloneAsDraft,
            shiftDays,
            postsCustomSchedule,
          });
        }
      },
    }));

    if (shiftPost.isError) {
      return (
        <ShiftTargetUnsuccessful
          page={profile}
          projectName={project?.name}
          additionalMessage={shiftPost?.error?.userMessage}
          errorMessage={t('postsShift.unableToMovePosts')}
        />
      );
    }

    if (shiftPost.data?.length) {
      const isMoveSettledResult = (
        data: Post[] | MovePostSettledResult[],
      ): data is MovePostSettledResult[] => {
        return 'status' in data[0];
      };

      if (variant === 'move' && isMoveSettledResult(shiftPost.data)) {
        const results = shiftPost.data;
        const successResults = results.filter(
          (r): r is MovePostSettledResult & { status: 'fulfilled' } =>
            r.status === 'fulfilled',
        );
        const errorResults = results.filter(
          (r): r is MovePostSettledResult & { status: 'rejected' } =>
            r.status === 'rejected',
        );

        return (
          <div className="tw-flex tw-flex-col tw-gap-2">
            {successResults.map((result) => {
              const originalPost = selectedPosts.find(
                (p) => p.id === result.postId,
              );
              const postUrl = `${getBasePath()}${
                routes.POSTS_CALENDAR
              }${createSearchFromParams({
                id: result.value.id,
                profiles: target.profileId,
              })}#${
                isNewPostExperienceActive
                  ? modalRoutes.POST_DETAIL_NEW
                  : modalRoutes.POST_DETAIL
              }`;

              const infoMessage = originalPost
                ? getPostTargetInfoMessage(originalPost, result.value)
                : '';

              return (
                <ShiftTargetSuccessful
                  key={result.postId}
                  infoMessage={infoMessage}
                  warningMessage={result.value.warning}
                  urlToOpen={postUrl}
                  label={t('postsShift.showPost')}
                  dataNamePrefix={dataNamePrefix}
                  projectName={project?.name}
                  page={profile}
                />
              );
            })}
            {errorResults.map((result) => (
              <ShiftTargetUnsuccessful
                key={result.postId}
                page={profile}
                projectName={project?.name}
                additionalMessage={result.reason}
                errorMessage={t('postsShift.unableToMovePosts')}
              />
            ))}
          </div>
        );
      }

      const posts = shiftPost.data as Post[];
      const contentPlanUrl = `${getBasePath()}${
        routes.POSTS_CALENDAR
      }${createSearchFromParams({
        profiles: target.profileId,
        project: target.projectId,
      })}`;

      const firstPostUrl = `${getBasePath()}${
        routes.POSTS_CALENDAR
      }${createSearchFromParams({
        id: posts[0].id,
        profiles: target.profileId,
      })}#${
        isNewPostExperienceActive
          ? modalRoutes.POST_DETAIL_NEW
          : modalRoutes.POST_DETAIL
      }`;

      const isSinglePost = posts.length === 1;
      const additionalMessage = isSinglePost
        ? getPostTargetInfoMessage(selectedPosts[0], posts[0])
        : '';

      return (
        <ShiftTargetSuccessful
          infoMessage={additionalMessage}
          urlToOpen={isSinglePost ? firstPostUrl : contentPlanUrl}
          label={
            isSinglePost
              ? t('postsShift.showPost')
              : t('postsShift.showContentPlan')
          }
          dataNamePrefix={dataNamePrefix}
          projectName={project?.name}
          page={profile}
        />
      );
    }

    if (initialTarget.locked) {
      return shiftPost.isLoading ? <Skeleton width="100%" height={85} /> : null;
    }

    return (
      <div className="tw-relative">
        {!shiftPost.isLoading && (
          <ShiftTargetProjectOfProfileSelect
            isLoading={isLoadProjectsLoading}
            error={error}
            onRemoveTarget={onRemoveTarget}
            targetNumber={variant === 'duplicate' ? targetNumber : undefined}
            pages={availablePages}
            projects={projects}
            selectedProfile={profile}
            onProfileChange={(profileId) => {
              setTarget({ profileId, projectId: undefined });
            }}
            selectedProject={project}
            onProjectChange={(projectId) => setTarget({ projectId })}
            onCloneAsDraftChange={
              variant === 'duplicate'
                ? () =>
                    setTarget(({ cloneAsDraft }) => ({
                      cloneAsDraft: !cloneAsDraft,
                    }))
                : undefined
            }
            cloneAsDraft={target.cloneAsDraft}
            dataNamePrefix={dataNamePrefix}
          />
        )}

        {!shiftPost.isLoading &&
          selectedPosts.some((post) => post.type === POST_TYPE.DOCUMENT) &&
          profile?.type !== PAGE_TYPE.LINKEDIN && (
            <Alert variant="warning" className="tw-mt-3">
              {variant === 'move'
                ? t('postsShift.documentPostTypeMoveLinkedinOnly', {
                    defaultValue:
                      'Document post type is supported only for LinkedIn. For other social networks, only the post text will be moved.',
                  })
                : t('postsShift.documentPostTypeLinkedinOnly', {
                    defaultValue:
                      'Document post type is supported only for LinkedIn. For other social networks, only the post text will be duplicated.',
                  })}
            </Alert>
          )}

        {shiftPost.isLoading && <Skeleton width="100%" height={85} />}
      </div>
    );
  },
);

export default ShiftPostToTarget;
